import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { Panel } from '../components/Panel'
import { Field, Select } from '../components/Field'
import { StatusDot } from '../components/StatusDot'
import { PubBadge } from '../components/PubBadge'
import { api, type EmailLogDetail, type EmailLogEntry, type EmailLogPage, type EmailLogResend } from '../api/client'
import { fmtDateTime } from '../lib/format'

// Refusals first — the reason this section exists is that a blocked send
// used to be invisible everywhere but a container log line.
const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'blocked_allowlist,blocked_disabled,blocked_no_domain,suppressed,skipped_undeliverable,render_failed,mailgun_error', label: 'Refused (any)' },
  { value: 'blocked_allowlist', label: 'Blocked · allowlist' },
  { value: 'blocked_disabled', label: 'Blocked · disabled' },
  { value: 'blocked_no_domain', label: 'Blocked · no domain' },
  { value: 'suppressed', label: 'Suppressed' },
  { value: 'skipped_undeliverable', label: 'Skipped · undeliverable' },
  { value: 'render_failed', label: 'Render failed' },
  { value: 'mailgun_error', label: 'Mailgun error' },
  { value: 'queued', label: 'Queued' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'opened', label: 'Opened' },
  { value: 'clicked', label: 'Clicked' },
  { value: 'failed', label: 'Failed' },
  { value: 'bounced', label: 'Bounced' },
  { value: 'complained', label: 'Complained' },
]

const TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'transactional', label: 'Transactional' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'campaign', label: 'Campaign' },
  { value: 'ops_alert', label: 'Ops alert' },
  { value: 'staff_notice', label: 'Staff notice' },
  { value: 'test', label: 'Test' },
]

export function EmailsLogPage() {
  // Set by the "Full history" link on a subscriber page. Kept in the URL
  // rather than component state so the link is shareable and the heading
  // can say whose history is on screen.
  const [searchParams, setSearchParams] = useSearchParams()
  const subscriberId = searchParams.get('subscriber_id')

  const [status, setStatus] = useState('')
  const [sendType, setSendType] = useState('')
  const [q, setQ] = useState('')
  // Cursor stack: each page pushes the cursor that produced it, so Back
  // pops rather than re-querying from the top.
  const [cursors, setCursors] = useState<string[]>([])
  const [openRow, setOpenRow] = useState<number | null>(null)
  const cursor = cursors[cursors.length - 1] ?? ''

  const params = new URLSearchParams({ limit: '100' })
  if (status) params.set('status', status)
  if (sendType) params.set('send_type', sendType)
  if (q) params.set('q', q)
  if (cursor) params.set('cursor', cursor)
  if (subscriberId) params.set('subscriber_id', subscriberId)

  const { data, isLoading } = useQuery({
    queryKey: ['email-log', status, sendType, q, cursor, subscriberId],
    queryFn: () => api.get<EmailLogPage>(`/api/email-log?${params.toString()}`),
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  })

  // Any filter change invalidates the paging position.
  function resetTo(fn: () => void) {
    fn()
    setCursors([])
  }

  const items = data?.items ?? []
  const gaps = data?.coverage_gaps ?? []

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="label mb-1">Telemetry</div>
          <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
            Emails Log
          </h1>
          {subscriberId && (
            <button
              className="label text-amber-glow mt-2"
              onClick={() => {
                searchParams.delete('subscriber_id')
                setSearchParams(searchParams)
                setCursors([])
              }}
            >
              Filtered to subscriber {subscriberId} · clear ✕
            </button>
          )}
        </div>
        <div className="flex items-end gap-4">
          <Field
            label="Recipient / subject"
            value={q}
            placeholder="search…"
            onChange={(e) => resetTo(() => setQ(e.target.value))}
          />
          <Select
            label="Status"
            value={status}
            onChange={(e) => resetTo(() => setStatus(e.target.value))}
            options={STATUS_OPTIONS}
          />
          <Select
            label="Type"
            value={sendType}
            onChange={(e) => resetTo(() => setSendType(e.target.value))}
            options={TYPE_OPTIONS}
          />
        </div>
      </header>

      {gaps.map((g) => (
        <div
          key={g.begin}
          className="border border-amber-glow/30 bg-amber-glow/5 px-4 py-3"
        >
          <div className="label text-amber-glow mb-1">
            Coverage gap · {g.begin} → {g.end}
          </div>
          <div className="text-[13px] text-paper-dim">{g.reason}</div>
        </div>
      ))}

      <Panel label="Send Register" bodyClassName="p-0" accent="cyan">
        {isLoading && (
          <div className="px-4 py-10 text-center label text-paper-trace">Loading…</div>
        )}
        {!isLoading && items.length === 0 && (
          <div className="px-4 py-10 text-center label text-paper-trace">
            No matching sends
          </div>
        )}
        {!isLoading && items.length > 0 && (
          <table className="w-full">
            <thead>
              <tr className="border-b border-ink-500">
                <Th>When</Th>
                <Th>Recipient</Th>
                <Th>Publication</Th>
                <Th>Subject</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr
                  key={e.id}
                  onClick={() => setOpenRow(e.id)}
                  className="border-b border-ink-500/30 hover:bg-ink-700/30 cursor-pointer"
                >
                  <Td mono subtle>{fmtDateTime(e.occurred_at)}</Td>
                  <Td mono className="max-w-[220px] truncate">{e.to_email}</Td>
                  <Td>
                    {e.publication_slug ? <PubBadge slug={e.publication_slug} compact /> : '—'}
                  </Td>
                  <Td className="max-w-[260px] truncate">{e.subject || '—'}</Td>
                  <Td><StatusBadge status={e.status} /></Td>
                  <Td>
                    <ResendButton entry={e} />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      {openRow !== null && (
        <EmailDetailDrawer id={openRow} onClose={() => setOpenRow(null)} />
      )}

      <div className="flex items-center justify-between">
        <button
          className="label text-paper-dim hover:text-paper disabled:opacity-30"
          disabled={cursors.length === 0}
          onClick={() => setCursors((c) => c.slice(0, -1))}
        >
          ← Newer
        </button>
        <button
          className="label text-paper-dim hover:text-paper disabled:opacity-30"
          disabled={!data?.next_cursor}
          onClick={() => setCursors((c) => [...c, data!.next_cursor!])}
        >
          Older →
        </button>
      </div>
    </div>
  )
}

function Th({ children }: { children?: React.ReactNode }) {
  return <th className="label py-2 px-4 font-normal text-left">{children}</th>
}

function Td({
  children, mono, subtle, className = '',
}: {
  children: React.ReactNode
  mono?: boolean
  subtle?: boolean
  className?: string
}) {
  return (
    <td
      className={`py-2 px-4
        ${mono ? 'font-mono text-[11px] tabular-nums' : 'text-[13px]'}
        ${subtle ? 'text-paper-dim' : 'text-paper'}
        ${className}`}
    >
      {children}
    </td>
  )
}

const REFUSED = new Set([
  'blocked_allowlist', 'blocked_disabled', 'blocked_no_domain',
  'suppressed', 'skipped_undeliverable', 'render_failed', 'mailgun_error',
])

export function StatusBadge({ status }: { status: string }) {
  const tone =
    status === 'delivered' || status === 'opened' || status === 'clicked' ? 'ok' :
    status === 'accepted' || status === 'queued' ? 'active' :
    status === 'failed' || status === 'bounced' || status === 'complained' ? 'err' :
    REFUSED.has(status) ? 'warn' : 'muted'
  return (
    <span className="inline-flex items-center gap-1.5">
      <StatusDot tone={tone} />
      <span className="label">{status}</span>
    </span>
  )
}


function EmailDetailDrawer({ id, onClose }: { id: number; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['email-log', 'detail', id],
    queryFn: () => api.get<EmailLogDetail>(`/api/email-log/${id}`),
  })

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex justify-end"
      onClick={onClose}
    >
      <div
        className="w-[min(900px,92vw)] h-full bg-ink-800 border-l border-ink-500
                   overflow-y-auto"
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 py-5 border-b border-ink-500">
          <div>
            <div className="label mb-1">Email</div>
            <h2 className="font-serif text-[24px] text-paper">
              {data?.subject || '(no subject)'}
            </h2>
          </div>
          <button className="label text-paper-dim hover:text-paper" onClick={onClose}>
            Close ✕
          </button>
        </div>

        {isLoading && (
          <div className="px-6 py-10 label text-paper-trace">Loading…</div>
        )}

        {data && (
          <div className="px-6 py-5 space-y-6">
            <dl className="grid grid-cols-2 gap-x-8 gap-y-2 text-[13px]">
              <Meta label="To" value={data.to_email} />
              <Meta label="From" value={data.from_email || data.from_domain} />
              <Meta label="Status" value={data.status} />
              <Meta label="Type" value={`${data.send_type} · ${data.source}`} />
              <Meta label="Sent" value={fmtDateTime(data.occurred_at)} />
              <Meta label="Mailgun id" value={data.mailgun_message_id || '—'} />
              {data.status_detail && (
                <Meta label="Reason" value={data.status_detail} />
              )}
            </dl>

            <div>
              <div className="label mb-2">Body</div>
              <BodyView detail={data} />
            </div>

            {data.events.length > 0 && (
              <div>
                <div className="label mb-2">Events ({data.events.length})</div>
                <pre className="bg-ink-900 p-3 text-[11px] font-mono text-paper-dim
                                overflow-x-auto max-h-[300px]">
                  {JSON.stringify(data.events, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="label text-paper-trace">{label}</dt>
      <dd className="font-mono text-[12px] text-paper break-all">{value}</dd>
    </div>
  )
}

function BodyView({ detail }: { detail: EmailLogDetail }) {
  if (detail.body_html) {
    return (
      // sandbox with no allow-* flags: the stored HTML carries
      // subscriber-supplied merge values, so it is rendered with scripts,
      // forms and same-origin access all switched off rather than injected
      // into the admin document.
      <iframe
        title="Email body"
        sandbox=""
        srcDoc={detail.body_html}
        className="w-full h-[520px] bg-white border border-ink-500"
      />
    )
  }
  if (detail.body_text) {
    return (
      <pre className="bg-ink-900 p-3 text-[12px] font-mono text-paper
                      whitespace-pre-wrap overflow-x-auto">
        {detail.body_text}
      </pre>
    )
  }
  if (detail.body_pruned_at) {
    return (
      <div className="border border-ink-500 px-4 py-6 text-center">
        <div className="label text-paper-trace">
          Body expired {fmtDateTime(detail.body_pruned_at)}
        </div>
        <div className="text-[12px] text-paper-dim mt-1">
          Bodies are kept for 30 days. The send record itself is permanent.
        </div>
      </div>
    )
  }
  return (
    <div className="border border-ink-500 px-4 py-6 text-center">
      <div className="label text-paper-trace">No body recorded</div>
      <div className="text-[12px] text-paper-dim mt-1">
        This send was refused before anything was rendered.
      </div>
    </div>
  )
}


function ResendButton({ entry }: { entry: EmailLogEntry }) {
  const qc = useQueryClient()
  const [result, setResult] = useState<EmailLogResend | null>(null)

  const resend = useMutation({
    mutationFn: () =>
      api.post<EmailLogResend>(`/api/email-log/${entry.id}/resend`, {}),
    onSuccess: (r) => {
      setResult(r)
      qc.invalidateQueries({ queryKey: ['email-log'] })
    },
  })

  if (!entry.can_resend) {
    return (
      <span className="label text-paper-trace" title={entry.resend_blocked_reason ?? ''}>
        —
      </span>
    )
  }

  // The outcome replaces the button rather than flashing past: with the
  // pilot allowlist narrow, most resends come back refused, and that is
  // the thing the operator actually needs to read.
  if (result) {
    return (
      <span
        className={`label ${result.ok ? 'text-radar-green' : 'text-amber-glow'}`}
        title={result.detail ?? ''}
      >
        {result.ok ? 'Resent' : result.status}
      </span>
    )
  }

  return (
    <button
      disabled={resend.isPending}
      onClick={(ev) => {
        ev.stopPropagation()
        if (
          window.confirm(
            `Resend this email to ${entry.to_email}?\n\n` +
            'It sends the stored copy exactly as it was, and is subject to ' +
            'the same suppression and allowlist rules as a normal send.',
          )
        ) {
          resend.mutate()
        }
      }}
      className="label text-paper-dim hover:text-amber-glow disabled:opacity-40"
    >
      {resend.isPending ? 'Sending…' : 'Resend'}
    </button>
  )
}
